import { useEffect } from 'react';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useBanks from '@/hooks/useBanks';
import LabeledInput, { SearchableSelect } from './components/LabeledInput';
import type { SupportedFormData } from './schemas/supportedFormSchema';
import minusIcon from '@/assets/icons/minus.svg';
import { createDefaultBankAccount } from './schemas/supportedFormSchema';

const BankAccountInfo = () => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<SupportedFormData>();

  // HAT-1464: a deleted supported entered again brings their bank accounts along, to keep or drop.
  const existingAccounts = watch('existingPaymentInfos') || [];

  const {
    fields: bankAccounts,
    append: appendAccount,
    remove: removeAccount,
  } = useFieldArray({
    control,
    name: 'paymentInfos',
  });

  useEffect(() => {
    if (bankAccounts.length === 0 && existingAccounts.length === 0) {
      appendAccount(createDefaultBankAccount());
    }
  }, [bankAccounts.length, existingAccounts.length, appendAccount]);

  const handleRemoveExistingAccount = (accountId: number) => {
    setValue(
      'existingPaymentInfos',
      existingAccounts.filter((account) => account.id !== accountId),
    );
  };

  const handleAddBankAccount = () => {
    appendAccount(createDefaultBankAccount());
  };

  return (
    <div className="flex flex-col gap-6 w-[850px] max-md:w-full">
      <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('bankAccountDetails')}</h4>

      {existingAccounts.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className="body-md-semibold text-(--font-body-1)">{t('reenterExistingBankAccounts')}</span>
          <span className="body-sm-regular text-(--font-body-3)">{t('reenterExistingBankAccountsHint')}</span>
          <ul className="flex flex-col gap-2">
            {existingAccounts.map((account) => (
              <li
                key={account.id}
                className="flex items-center justify-between gap-4 bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-xl px-4 py-3 max-md:flex-col max-md:items-start"
              >
                <div className="flex flex-wrap gap-x-6 gap-y-1 body-sm-regular text-(--font-body-5)">
                  <span className="body-sm-semibold">{account.accountOwnerName}</span>
                  <span>{t('bankCode')}: {account.bankName ? `${account.bankNumber} - ${account.bankName}` : account.bankNumber}</span>
                  <span>{t('branchNumber')}: {account.branchNumber}</span>
                  <span>{t('accountNumber')}: {account.accountNumber}</span>
                  <span className="text-(--font-body-3)">{t(account.status)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveExistingAccount(account.id)}
                  className="body-sm-semibold text-(--font-error) underline shrink-0"
                  aria-label={`${t('removeBankAccount')} ${account.accountNumber}`}
                >
                  {t('removeBankAccount')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {bankAccounts.map((account, index) => (
        <div key={account.id} className="flex flex-col gap-4">
          {/* With an existing account kept no new one is required, so every new one can be removed. */}
          {index > 0 || existingAccounts.length > 0 ? (
            <div className="bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-2xl p-8 max-md:p-4 relative shadow-sm">
              {/* Row 1: Beneficiary Name (Account Owner) */}
              <div className="flex gap-8 max-md:flex-col max-md:gap-4 mb-4">
                <Controller
                  name={`paymentInfos.${index}.accountOwnerName`}
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      label={t('beneficiaryName')}
                      id={`accountOwnerName-${index}`}
                      width="414px"
                      mobileFullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder={t('beneficiaryNamePlaceholder')}
                      required
                    />
                  )}
                />
              </div>

              {/* Row 2: Bank Code, Account Number, Branch Number */}
              <div className="flex gap-8 max-md:flex-col max-md:gap-4">
                <Controller
                  name={`paymentInfos.${index}.bankNumber`}
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      label={t('bankCode')}
                      id={`bankNumber-${index}`}
                      width="262.67px"
                      mobileFullWidth
                      onChange={(value) => {
                        const bankNum = parseInt(value.split(' - ')[0]);
                        field.onChange(isNaN(bankNum) ? 0 : bankNum);
                      }}
                      loadOptions={useBanks.getBankOptions}
                      placeholder="bankCodePlaceholder"
                      required
                    />
                  )}
                />
                <Controller
                  name={`paymentInfos.${index}.branchNumber`}
                  control={control}
                  render={({ field }) => {
                    const bankNumber = watch(`paymentInfos.${index}.bankNumber`);
                    const bankValue = bankNumber ? `${bankNumber} - ` : '';
                    return (
                      <SearchableSelect
                        label={t('branchNumber')}
                        id={`branchNumber-${index}`}
                        width="262.67px"
                        mobileFullWidth
                        onChange={(value) => {
                          const branchNum = value.split(' - ')[0];
                          field.onChange(branchNum);
                        }}
                        loadOptions={useBanks.getBranchOptions}
                        dependency={bankValue}
                        placeholder="branchNumberPlaceholder"
                        dependencyPlaceholder="selectBankFirst"
                        required
                      />
                    );
                  }}
                />
                <Controller
                  name={`paymentInfos.${index}.accountNumber`}
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      label={t('accountNumber')}
                      id={`accountNumber-${index}`}
                      width="262.67px"
                      mobileFullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                      placeholder={t('accountNumberPlaceholder')}
                      required
                    />
                  )}
                />
                <button
                  type="button"
                  onClick={() => removeAccount(index)}
                  className="absolute start-[-12px] top-6 w-6 h-6 bg-(--font-error) rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                  title={t('removeBankAccount')}
                >
                  <img src={minusIcon} alt="" />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Row 1: Beneficiary Name (Account Owner) */}
              <div className="flex gap-8 max-md:flex-col max-md:gap-4">
                <Controller
                  name={`paymentInfos.${index}.accountOwnerName`}
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      label={t('beneficiaryName')}
                      id={`accountOwnerName-${index}`}
                      width="414px"
                      mobileFullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder={t('beneficiaryNamePlaceholder')}
                      required
                    />
                  )}
                />
              </div>

              {/* Row 2: Bank Code, Account Number, Branch Number */}
              <div className="flex gap-8 max-md:flex-col max-md:gap-4">
                <Controller
                  name={`paymentInfos.${index}.bankNumber`}
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      label={t('bankCode')}
                      id={`bankNumber-${index}`}
                      width="262.67px"
                      mobileFullWidth
                      onChange={(value) => {
                        const bankNum = parseInt(value.split(' - ')[0]);
                        field.onChange(isNaN(bankNum) ? 0 : bankNum);
                      }}
                      loadOptions={useBanks.getBankOptions}
                      placeholder="bankCodePlaceholder"
                      required
                    />
                  )}
                />
                <Controller
                  name={`paymentInfos.${index}.branchNumber`}
                  control={control}
                  render={({ field }) => {
                    const bankNumber = watch(`paymentInfos.${index}.bankNumber`);
                    const bankValue = bankNumber ? `${bankNumber} - ` : '';
                    return (
                      <SearchableSelect
                        label={t('branchNumber')}
                        id={`branchNumber-${index}`}
                        width="262.67px"
                        mobileFullWidth
                        onChange={(value) => {
                          const branchNum = value.split(' - ')[0];
                          field.onChange(branchNum);
                        }}
                        loadOptions={useBanks.getBranchOptions}
                        dependency={bankValue}
                        placeholder="branchNumberPlaceholder"
                        dependencyPlaceholder="selectBankFirst"
                        required
                      />
                    );
                  }}
                />
                <Controller
                  name={`paymentInfos.${index}.accountNumber`}
                  control={control}
                  render={({ field }) => (
                    <LabeledInput
                      label={t('accountNumber')}
                      id={`accountNumber-${index}`}
                      width="262.67px"
                      mobileFullWidth
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                      placeholder={t('accountNumberPlaceholder')}
                      required
                    />
                  )}
                />
              </div>
            </>
          )}
        </div>
      ))}

      {/* Add Another Bank Account Button */}
      <button
        type="button"
        onClick={handleAddBankAccount}
        className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
      >
        <div className="w-[10px] h-[10px] relative">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 1V9M1 5H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {t('addAnotherBankAccount')}
      </button>
    </div>
  );
};

export default BankAccountInfo;
